import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  CmdInput,
  CmdLabel,
  CmdModal,
  CmdModalFooterButton,
  CmdSimpleSuggestion,
} from '@commander-services/gui-components';
import { Controller, useForm } from 'react-hook-form';
import { useQueries } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { AxiosResponse } from 'axios';
import FormikValidatorService from '../../Services/FormikValidatorService';
import usePageWithForm from '../../hooks/usePageWithForm';
import { transformErrors } from '../../Services/HookFormService';
import { getRequest } from '../../Services/HttpService';
import useAnalytics from '../../hooks/useAnalytics';

interface IRecipientFormProps {
  closeCallback: () => void;
  openCallback: () => void;
  responseCallback: (values: any) => void;
  editCallback: (values: any) => void;
  editingRecipient: any;
  putRecipients: any;
  reportId: string;
}

export interface IRecipient {
  id: null;
  userId: number;
  email: string;
  fullname: string;
  phone: string | null;
}

interface IRecipientsInputForm {
  name: string;
  email: string;
}

export default function RecipientForm(props: IRecipientFormProps): JSX.Element {
  usePageWithForm();
  const { formatMessage: f } = useIntl();
  const [isLoaderActive, setIsLoaderActive] = React.useState<boolean>(false);
  const [validation, setValidation] = React.useState<any>(null);
  const [isNextForm, setIsNextForm] = React.useState<boolean>(false);
  const [userOptions, setUserOptions] = React.useState<IRecipient[]>([]);
  const [allUsersOptions, setAllUsersOptions] = React.useState<any[]>([]);
  const [formReset, setFormReset] = React.useState(false);
  const { trackEvent } = useAnalytics();

  const validateFields = (values: any) => {
    let formErrors: any = {};
    if (!validation) {
      return formErrors;
    }
    delete validation.name;
    formErrors = FormikValidatorService.validateByValidationApiResponse(values, validation);
    if (!values.email) {
      formErrors.email = f({ id: 'form.required' });
    }
    return transformErrors(formErrors);
  };

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    trigger,
    formState: { errors },
  } = useForm<IRecipientsInputForm>({
    mode: 'onChange',
    defaultValues: {
      name: props.editingRecipient ? props.editingRecipient.userId : '',
      email: props.editingRecipient ? props.editingRecipient.email : '',
    },
    resolver: async (data) => {
      const formErrors = validateFields(data);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const getValidation = async () => {
    setIsLoaderActive(true);
    const response: AxiosResponse | false = await getRequest(
      '/v1/auto-report/validation/recipients',
      { returnAxiosResponse: true }
    );
    setIsLoaderActive(false);
    if (response && response.data && response.status === 200) {
      const inputs = response.data;
      setValidation(inputs);
    }
  };

  useQueries({
    queries: [
      {
        queryKey: ['autoReportsRecipientFormValidation'],
        queryFn: () => getValidation(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const onSubmit = (values: any) => {
    trackEvent(['auto reports', 'click', 'Autoreports recipient save']);
    if (values.email) {
      const newValues = { ...values };

      if (allUsersOptions.length > 0 && values.name) {
        const user = allUsersOptions.find((i: any) => String(i.userId) === String(values.name));
        if (user) {
          newValues.userId = user.userId;
          newValues.name = user.fullname;
          newValues.fullname = user.fullname;
        }
      }

      if (Number.isInteger(Number(values.name))) {
        newValues.name = '';
      }

      if (props.editingRecipient) {
        newValues.key = props.editingRecipient.key;
        newValues.fullname = props.editingRecipient.fullname || newValues.name;
        newValues.userId = props.editingRecipient.userId || newValues.userId || null;
        newValues.name = newValues.fullname;
      }
      if (props.editingRecipient) {
        props.editCallback(newValues);
      } else {
        props.responseCallback(newValues);
      }

      if (isNextForm) {
        reset();
        setFormReset(true);
        setAllUsersOptions([]);
        setUserOptions([]);
        setIsNextForm(false);
      } else {
        props.closeCallback();
      }
    }
  };

  const getNameOptions = async (val: string) => {
    const res: AxiosResponse | false = await getRequest(
      `/v1/auto-report/form/recipient?query=${val}&reportId=${props.reportId}`,
      { returnAxiosResponse: true }
    );
    if (res && res.data) {
      const putRecipientNames = props.putRecipients.map((item: { email: string; name: string }) => {
        return item.name;
      });
      const recipientArr = res.data.recipients.filter(
        (item: IRecipient) => !putRecipientNames.includes(item.fullname)
      );
      setUserOptions(recipientArr);
      setAllUsersOptions(recipientArr);
    }
  };

  const debouncedNameOptions = debounce((value: string) => {
    const regexName = /^([ a-zA-Z]){2,}[ a-zA-Z]*$/i;
    if (value && value.length < 36 && regexName.test(value)) {
      getNameOptions(value);
    } else {
      setUserOptions([]);
    }
  }, 900);

  const handleOptions = (event: any) => {
    if (event && event.target) {
      const val = event.target.value;
      debouncedNameOptions(val);
    }
  };

  const handleChosenRecipient = (value: string | number) => {
    trackEvent(['auto reports', 'click', 'Autoreports recipient choose']);
    const inputs = allUsersOptions.find((i) => String(i.userId) === String(value));
    if (inputs) {
      Object.keys(inputs).map((indexId: string) => {
        if (indexId === 'userId') {
          setValue('name', inputs.userId);
        } else if (indexId === 'email') {
          setValue('email', inputs.email || '');
        }
      });
    } else {
      setValue('name', String(value));
    }
    setFormReset(false);
    trigger('email');
  };

  const nextForm = (value: boolean) => {
    setIsNextForm(value);
  };

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'close-modal',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'alarms.modal.rule.button.close' }),
      tooltip: f({ id: 'alarms.modal.recipient.close.tooltip' }),
      closeCallback: props.closeCallback,
    },
    {
      id: 'autoreport-recipient-form-submit',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'alarms.modal.recipient.save' }),
      tooltip: f({ id: 'alarms.modal.recipient.save.tooltip' }),
      submitCallback: handleSubmit(onSubmit),
    },
  ];

  return (
    <CmdModal
      id="recipient-form-modal"
      title={
        props.editingRecipient
          ? f({ id: 'alarms.modal.recipient.edit' })
          : f({ id: 'alarms.modal.recipient.title' })
      }
      loader={isLoaderActive}
      footerButtons={buttons}
      modalFooterCheckboxReset={formReset}
      modalFooterCheckbox={
        props.editingRecipient ? '' : f({ id: 'alarms.section.recipient.nextForm' })
      }
      modalFooterCheckboxTitle={f({ id: 'alarms.section.recipient.nextForm.tooltip' })}
      modalFooterCheckboxCallback={nextForm}
    >
      <div>
        {props.editingRecipient &&
        (props.editingRecipient.userId ||
          props.editingRecipient.name ||
          props.editingRecipient.fullname) ? (
          <CmdLabel
            title={f({ id: 'alarms.section.recipient.name' })}
            value={props.editingRecipient.fullname || props.editingRecipient.name}
          />
        ) : (
          <Controller
            name="name"
            control={control}
            rules={{ required: errors.name ? errors.name.message : undefined }}
            render={({ field }) => (
              <CmdSimpleSuggestion
                {...field}
                label={f({ id: 'alarms.section.recipient.name' })}
                id="name"
                placeholder={f({ id: 'form.search' })}
                options={userOptions.map((i) => {
                  return { value: String(i.userId), item: i.fullname };
                })}
                required={false}
                onChangeCallback={handleOptions}
                onSubmitCallback={handleChosenRecipient}
                introOption={f({ id: 'form.choose' })}
                tooltip={f({ id: 'autoreport.modal.formField.recipient.tooltip' })}
                reset={formReset}
                setValue={(value) => {
                  setValue('name', value);
                  trigger('name');
                }}
              />
            )}
          />
        )}
        <Controller
          name="email"
          control={control}
          rules={{ required: errors.email ? errors.email.message : undefined }}
          render={({ field }) => (
            <CmdInput
              {...field}
              label={f({ id: 'alarms.section.recipient.email' })}
              id="email"
              name="email"
              type="text"
              required={true}
              onChangeCallback={(value) => {
                setValue('email', value);
                trigger('email');
              }}
              tooltip={f({ id: 'alarms.modal.recipient.formField.email.tooltip' })}
              error={errors.email?.message}
              touched={true}
            />
          )}
        />
      </div>
    </CmdModal>
  );
}
